import React, { useEffect, useMemo, useState, useRef } from 'react';
import {
  ShoppingBag,
  Phone,
  User,
  ChevronDown,
  ChevronUp,
  Banknote,
  Truck,
  RotateCcw,
  ShieldCheck,
  Trash2,
  Plus,
  Minus,
} from 'lucide-react';
import axios from "axios";
import { useNavigate } from 'react-router-dom';
import { Spin } from 'antd';
import { getBrowserFingerprint } from '../apis/DeviceFingerprint';
import { useCheckoutStore } from "../store/Store";
import pushToDataLayer from '../apis/GTM';
import { getTrackingMetadata } from '../apis/TrackingMetaData';
import { BASE_URL } from '../apis/constant';
import OrderFailedModal from '../components/FailedPage';
import PremiumChatModal from '../components/ChatModal';

const DeliveryZone = {
  INSIDE_DHAKA: 'Inside Dhaka',
  OUTSIDE_DHAKA: 'Outside Dhaka'
};

const DELIVERY_OPTIONS = [
  { id: DeliveryZone.INSIDE_DHAKA, label: 'Inside Dhaka', price: 80 },
  { id: DeliveryZone.OUTSIDE_DHAKA, label: 'Outside Dhaka', price: 150 }
];

const SmallInput = React.forwardRef(({ label, icon, hasError, isPulsing, ...props }, ref) => (
  <div className="w-full" ref={ref}>
    <style>
      {`
        @keyframes error-glow {
          0% { box-shadow: 0 0 0 0px rgba(239, 68, 68, 0.4); }
          50% { box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.2); }
          100% { box-shadow: 0 0 0 0px rgba(239, 68, 68, 0.4); }
        }
        .animate-error-glow {
          animation: error-glow 1s infinite ease-in-out;
        }
      `}
    </style>
    <label className="block text-[11px] sm:text-[13px] font-semibold text-gray-500 mb-2">{label}</label>
    <div className="relative">
      {icon && (
        <div className={`absolute left-3 top-1/2 -translate-y-1/2 z-10 ${hasError ? 'text-gray-900' : 'text-gray-400'}`}>
          {icon}
        </div>
      )}
      <input
        {...props}
        className={`w-full bg-white border text-sm rounded-lg p-3 outline-none transition-all duration-300 ${icon ? 'pl-10' : ''} ${hasError
          ? `border-red-500 text-gray-900 placeholder-gray-500 ${isPulsing ? 'animate-error-glow' : ''}`
          : 'border-gray-200 text-gray-900 placeholder-gray-400 focus:border-black'
          }`}
      />
    </div>
  </div>
));

const TrustBar = () => (
  <div className="w-full bg-gray-50 border border-gray-100 rounded-lg px-3 py-2 flex items-center justify-between text-xs text-gray-700 gap-3">
    <div className="flex items-center gap-2">
      <ShieldCheck size={14} />
      <span>100% Secure</span>
    </div>
    <div className="flex items-center gap-2">
      <Truck size={14} />
      <span>Fast Delivery</span>
    </div>
    <div className="flex items-center gap-2">
      <RotateCcw size={14} />
      <span>Easy Return</span>
    </div>
  </div>
);

const CartItemRow = ({ item, onRemove, onUpdateQuantity }) => {
  const price = Number(item.price) || 0;
  const originalPrice = item.originalPrice ? Number(item.originalPrice) : price;
  const quantity = Number(item.quantity) || 1;
  const totalPrice = price * quantity;
  const hasDiscount = item.originalPrice && originalPrice > price;

  return (
    <div className="flex items-center gap-3 bg-white p-3 border border-gray-100 rounded-2xl mb-3 shadow-sm transition-all duration-200">
      <div className="relative w-20 h-20 flex-shrink-0 overflow-hidden rounded-xl bg-gray-50 border border-gray-50 self-center">
        <img
          src={`${BASE_URL}${item.image}`}
          alt={item.name}
          className="w-full h-full object-cover"
          onError={(e) => {
            e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjQwMCIgdmlld0JveD0iMCAwIDQwMCA0MDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iNDAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0yMDAgMTMwQzE2NS44MTIgMTMwIDEzOCAxNTcuODEyIDEzOCAxOTJDMTM4IDIyNi4xODggMTY1LjgxMiAyNTQgMjAwIDI1NEMyMzQuMTg4IDI1NCAyNjIgMjI2LjE4OCAyNjIgMTkyQzI2MiAxNTcuODEyIDIzNC4xODggMTMwIDIwMCAxMzBaTTI1NiAyODRHMTQ0QzEzMS4yNjQgMjg0IDEyMCAyOTUuMjY0IDEyMCAzMDhWMzQwQzEyMCAzNDY2MjggMTM1LjM3MiAzNTIgMTMyIDM1MkgyNjhDMjc0LjYyOCAzNTIgMjgwIDM0Ni42MjggMjgwIDM0MFYzMDhDMjgwIDI5NS4yNjQgMTIwIDMwOFYzMDhDMjgwIDI5NS4yNjQgMjY4LjczNiAyODQgMjU2IDI4NFoiIGZpbGw9Il9ENEQ4REIiLz4KPC9zdmc+';
          }}
        />
      </div>

      <div className="flex flex-col flex-grow justify-between py-0.5 overflow-hidden">
        <div className="flex justify-between items-start gap-2">
          <div className="min-w-0">
            <h3 className="font-bold text-gray-900 text-sm truncate leading-tight mb-1">
              {item.name || 'Unnamed Product'}
            </h3>
            {item.variant && (
              <p className="text-xs text-gray-500 font-medium">
                {item.variant_type?.split(':')[0] || 'Variant'}: <span className="text-gray-700">{item.variant.split(':')[1] || item.variant}</span>
              </p>
            )}
          </div>

          <button
            onClick={() => onRemove(item.id)}
            className="text-gray-300 hover:text-red-500 transition-colors p-1 -mr-1"
            aria-label="Remove item"
          >
            <Trash2 size={18} strokeWidth={2} />
          </button>
        </div>

        <div className="flex justify-between items-end mt-2">
          <div className="flex items-center bg-gray-50 rounded-lg border border-gray-200 h-8 shadow-sm">
            <button
              onClick={() => onUpdateQuantity(item.id, -1)}
              disabled={quantity <= 1}
              className="w-8 h-full flex items-center justify-center text-gray-500 hover:text-black disabled:opacity-30 active:bg-gray-200 rounded-l-lg transition-colors"
            >
              <Minus size={14} strokeWidth={2.5} />
            </button>
            <span className="w-6 text-center text-xs font-bold text-gray-900">
              {quantity}
            </span>
            <button
              onClick={() => onUpdateQuantity(item.id, 1)}
              disabled={quantity >= (item.stock || 10)}
              className="w-8 h-full flex items-center justify-center text-gray-500 hover:text-black disabled:opacity-30 active:bg-gray-200 rounded-r-lg transition-colors"
            >
              <Plus size={14} strokeWidth={2.5} />
            </button>
          </div>

          <div className="text-right flex flex-col items-end justify-end">
            <div className="text-[10px] font-medium text-gray-400 mb-0.5">
              {quantity} x ৳{price.toLocaleString()}
              {hasDiscount && (
                <span className="ml-1 line-through text-gray-300">
                  ৳{originalPrice.toLocaleString()}
                </span>
              )}
            </div>
            <p className="text-base font-extrabold text-gray-900 leading-none">
              ৳{totalPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const CheckoutPage = () => {
  const { checkoutItems, checkoutDuration, setOrderId, isFlowActive } = useCheckoutStore();

  const [isHydrated, setIsHydrated] = useState(false);
  const [cart, setCart] = useState([]);
  const [deliveryZone, setDeliveryZone] = useState(DeliveryZone.INSIDE_DHAKA);
  const [isExpanded, setIsExpanded] = useState(false);
  const [formData, setFormData] = useState({ name: '', phone: '', address: '' });
  const [formErrors, setFormErrors] = useState({ name: false, phone: false, address: false });
  const [isPulsing, setIsPulsing] = useState({ name: false, phone: false, address: false });
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [createOrderLoading, setCreateOrderLoading] = useState(false);
  const [deviceFingerprint, setDeviceFingerprint] = useState(null);
  const [failedModalData, setFailedModalData] = useState({ viewModal: false, error: null, })
  const [isModalOpen, setIsModalOpen] = useState(true);
  const hasFired = useRef(false);



  useEffect(() => {
    const fetchId = async () => {
      const id = await getBrowserFingerprint();
      setDeviceFingerprint(id);
    };
    fetchId();
  }, []);



  function processCartData(cartItems) {
    let totalPrice = 0;
    let totalQuantity = 0;
    const productIds = {};

    for (let i = 0; i < cartItems.length; i++) {
      const item = cartItems[i];

      totalPrice += item.price * item.quantity;
      totalQuantity += item.quantity;
      productIds[item.productId] = true;
    }

    return {
      totalPrice: Math.round(totalPrice * 100) / 100,
      allProductIdsArray: Object.keys(productIds),
      cartItems: totalQuantity
    };
  }

  /* ------------------------ GTM Configuration ------------------------ */
  useEffect(() => {
    const hasFired = sessionStorage.getItem('iniChk_flg');

    const tracking_data = getTrackingMetadata();

    if (checkoutItems?.length > 0 && !hasFired) {
      const processedData = processCartData(checkoutItems);

      pushToDataLayer(
        'begin_checkout',
        {
          event_id: tracking_data.event_id,
          value: processedData.totalPrice.toFixed(2),
          currency: 'BDT',
          content_ids: processedData.allProductIdsArray,
          content_type: 'product',
          num_items: processedData.cartItems,
        },
        {
          external_id: localStorage.getItem("customerId"),
        }
      );

      /* ------------------------ GTM Configuration ------------------------ */

      /* ------------------------ CAPI API call ------------------------ */
      (async () => {
        try {
          const response = await axios.post(
            `${BASE_URL}/api/initiate_checkout_capi/`,
            {
              event_id: tracking_data.event_id,
              value: processedData.totalPrice.toFixed(2),
              currency: 'BDT',
              content_ids: processedData.allProductIdsArray,
              num_items: processedData.cartItems,
              external_id: localStorage.getItem("customerId"),
              tracking_data: tracking_data
            }
          );

          if (!response.data?.success) {
            console.error('CAPI initiate checkout failed', response.data);
          }
        } catch (error) {
          console.error('CAPI initiate checkout error', error);
        }
      })();
      /* ------------------------ CAPI API call ------------------------ */

      sessionStorage.setItem('iniChk_flg', 'true');
    }
  }, [checkoutItems]);




  const navigate = useNavigate();
  const nameRef = useRef(null);
  const phoneRef = useRef(null);
  const addressRef = useRef(null);

  // 1. Initial Hydration Check
  useEffect(() => {
    const timer = setTimeout(() => {
      setCart(Array.isArray(checkoutItems) ? checkoutItems : []);
      setIsHydrated(true);
    }, 150);
    return () => clearTimeout(timer);
  }, [checkoutItems]);

  // 2. The Bounce Guard
  useEffect(() => {
    if (isHydrated && (!isFlowActive || cart.length === 0)) {
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate('/', { replace: true });
      }
    }
  }, [isHydrated, isFlowActive, cart, navigate]);

  const subtotal = useMemo(() => cart.reduce((acc, it) => acc + it.price * it.quantity, 0), [cart]);
  const deliveryCharge = useMemo(() => DELIVERY_OPTIONS.find((o) => o.id === deliveryZone)?.price || 0, [deliveryZone]);
  const total = subtotal + deliveryCharge;

  const handleRemove = (id) => setCart((p) => p.filter((x) => x.id !== id));
  const handleQty = (id, delta) =>
    setCart((p) =>
      p.map((it) => (it.id === id ? { ...it, quantity: Math.max(1, it.quantity + delta) } : it))
    );

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (hasSubmitted) {
      setFormErrors(prev => ({ ...prev, [field]: !value.trim() }));
    }
  };

  const validateForm = () => {
    const errors = {
      name: !formData.name.trim(),
      phone: !formData.phone.trim(),
      address: !formData.address.trim()
    };
    setFormErrors(errors);
    setHasSubmitted(true);
    if (errors.name || errors.phone || errors.address) {
      setIsPulsing(errors);
      setTimeout(() => setIsPulsing({ name: false, phone: false, address: false }), 2000);
      if (errors.name) nameRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      else if (errors.phone) phoneRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      else if (errors.address) addressRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }
    return true;
  };

  const handlePlaceOrder = async () => {
    if (!validateForm()) return;
    setCreateOrderLoading(true);

    const tracking_data = getTrackingMetadata();

    const orderData = {
      ...formData,
      deliveryZone,
      extractedCart: cart.map(item => ({ id: item.id, productId: item.productId, variantId: item.variantId, qty: item.quantity })),
      securityData: {
        checkoutDuration: checkoutDuration(),
        tracking_data: tracking_data,
        deviceFingerprint: deviceFingerprint,
      }
    };

    try {
      const response = await axios.post(`${BASE_URL}/api/create_new_order/`, orderData);
      
      
      if (response.data?.success) {

        /* ------------------------ GTM Configuration ------------------------ */
        pushToDataLayer('purchase',
          {
            event_id: response.data.order_id,
            transaction_id: response.data.order_id,
            value: total.toFixed(2),
            currency: 'BDT',
            content_ids: cart.map(item => item.productId),
            content_type: 'product',
            num_items: cart.reduce((acc, item) => acc + item.quantity, 0),
          },
          {
            fn: formData.name,
            ph: formData.phone.replace(/\D/g, ''),
            external_id: localStorage.getItem("customerId")
          }
        );
        /* ------------------------ GTM Configuration ------------------------ */

        setOrderId(response.data.order_id);
        navigate('/confirmed-order', { replace: true });
      }
    } catch (err) {

      console.error(err);


      if (!err.response) {
        // Network issue
        setFailedModalData({ viewModal: true, error: "internet" });
      }
      else if (err.response.status === 429) {
        // Too many requests
        setFailedModalData({ viewModal: true, error: "limit" });
      }
      else {
        // Other backend error
        setFailedModalData({ viewModal: true, error: "order failed" });
      }





    } finally {
      setCreateOrderLoading(false);
    }
  };

  useEffect(() => {
    if (hasSubmitted) {
      setFormErrors({
        name: !formData.name.trim(),
        phone: !formData.phone.trim(),
        address: !formData.address.trim()
      });
    }
  }, [formData, hasSubmitted]);

  useEffect(() => {
    if (cart.length === 0) setIsExpanded(false);
  }, [cart.length]);

  if (!isHydrated) {
    return (
      <div className="h-screen w-full flex flex-col items-center justify-center bg-gray-50">
        <Spin size="large" />
      </div>
    );
  }

  // Prevent flash of content before redirect
  if (!isFlowActive || cart.length === 0) return null;

  return (
    <div className="bg-gray-50 text-gray-900 py-[10px] px-0 md:px-8 min-h-[calc(100vh-330px)] pb-[25px] sm:pb-[0px]">

      {failedModalData.viewModal && (
        <OrderFailedModal
          errorType={failedModalData.error}
          onClose={() => setFailedModalData({ viewModal: false, error: null })}
        />
      )}

      <PremiumChatModal />



      <main className="max-w-5xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
          <div className="space-y-4">
            <section className="bg-white rounded-2xl py-5 shadow-sm border border-gray-100 w-full">
              <div className="flex items-center gap-3 mb-4 px-4">
                <h2 className="text-base font-semibold">1. Shipping Details</h2>
              </div>
              <div className="space-y-3 px-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <SmallInput
                    ref={nameRef}
                    label="Full name"
                    placeholder="Rahim Ahmed"
                    icon={<User size={14} />}
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    hasError={formErrors.name}
                    isPulsing={isPulsing.name}
                  />
                  <SmallInput
                    ref={phoneRef}
                    label="Phone"
                    placeholder="017xxxxxxxx"
                    icon={<Phone size={14} />}
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    hasError={formErrors.phone}
                    isPulsing={isPulsing.phone}
                  />
                </div>
                <div ref={addressRef} className="w-full">
                  <style>
                    {`
                      @keyframes error-glow-textarea {
                        0% { box-shadow: 0 0 0 0px rgba(239, 68, 68, 0.4); }
                        50% { box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.2); }
                        100% { box-shadow: 0 0 0 0px rgba(239, 68, 68, 0.4); }
                      }
                      .animate-error-glow-textarea {
                        animation: error-glow-textarea 1s infinite ease-in-out;
                      }
                    `}
                  </style>
                  <label className="block text-[11px] sm:text-[13px] font-semibold text-gray-500 mb-2 px-1">Address</label>
                  <textarea
                    placeholder="House, road, area..."
                    className={`w-full rounded-lg border transition-all duration-300 p-3 text-sm min-h-[84px] resize-none outline-none text-gray-900 placeholder-gray-400 ${formErrors.address
                      ? `border-red-500 text-gray-900 placeholder-gray-500 ${isPulsing.address ? 'animate-error-glow-textarea' : ''}`
                      : 'border-gray-200 text-gray-900 placeholder-gray-400 focus:border-black'
                      }`}
                    value={formData.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-[11px] sm:text-[13px] font-semibold text-gray-500 mb-2 px-1">Delivery Area</label>
                  <div className="grid grid-cols-2 gap-2">
                    {DELIVERY_OPTIONS.map((opt) => (
                      <button
                        key={opt.id}
                        onClick={() => setDeliveryZone(opt.id)}
                        className={`text-sm p-3 rounded-lg text-left border transition ${deliveryZone === opt.id ? 'bg-gray-100 border-black text-black' : 'bg-white border-gray-100 text-gray-700 hover:border-gray-200'}`}
                        type="button"
                      >
                        <div className="font-medium">{opt.label}</div>
                        <div className="text-[12px] text-gray-700 mt-0.5">Charge ৳{opt.price}</div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </section>
            <section className="bg-white rounded-2xl py-4 shadow-sm border border-gray-100 w-full px-4">
              <div className="flex items-center gap-3 mb-2">
                <h3 className="text-base font-semibold">2. Payment Method</h3>
              </div>
              <div className="flex items-center gap-3 mt-2 border border-black rounded-lg p-3 bg-gray-50">
                <div className="p-2 bg-white rounded-full border border-gray-200 text-green-600 shrink-0">
                  <Banknote size={18} />
                </div>
                <div className="flex-1">
                  <div className="font-medium text-sm">Cash on Delivery</div>
                  <div className="text-[12px] text-gray-500 mt-0.5">Pay when you receive your order</div>
                </div>
              </div>
            </section>
          </div>

          <div className="space-y-4 md:px-0">
            <section className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <span className="hidden md:inline">Order Summary</span>
                  <span className="inline-flex items-center justify-center w-7 h-7 bg-gray-100 text-sm rounded-md">
                    <ShoppingBag size={14} />
                  </span>
                </h3>
                <button
                  onClick={() => setIsExpanded((s) => !s)}
                  className="md:hidden text-sm text-gray-600 flex items-center gap-2"
                  type="button"
                >
                  {isExpanded ? 'Hide' : `Items (${cart.length})`}
                  {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>
              </div>
              <div className={`${isExpanded ? 'block' : 'hidden'} md:block mt-3 sm:max-h-[340px] overflow-y-auto pr-2`} style={{ scrollbarWidth: 'thin', scrollbarColor: '#d1d5db transparent' }}>
                {cart.length === 0 ? (
                  <div className="py-8 text-center text-gray-400 text-sm">Your cart is empty</div>
                ) : (
                  cart.map((it) => <CartItemRow key={it.id} item={it} onRemove={handleRemove} onUpdateQuantity={handleQty} />)
                )}
              </div>
              <div className="mt-4">
                <div className="bg-gray-50 border border-gray-100 rounded-lg p-4">
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>Subtotal</span>
                    <span>৳{subtotal.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm text-gray-600 mt-2">
                    <span>Delivery Charge</span>
                    <span>৳{deliveryCharge}</span>
                  </div>
                  <div className="flex justify-between items-center mt-4 pt-3 border-t border-gray-200">
                    <span className="font-semibold text-gray-900">Total</span>
                    <span className="text-base font-extrabold">৳{total.toLocaleString()}</span>
                  </div>
                </div>
                <div className="hidden sm:block">
                  <button
                    className="w-full mt-4 bg-black text-white font-semibold py-3 rounded-lg text-base hover:opacity-95 active:opacity-90 focus:outline-none focus:ring-2 focus:ring-black/20 disabled:opacity-50 flex items-center justify-center gap-2"
                    disabled={cart.length === 0 || createOrderLoading}
                    onClick={handlePlaceOrder}
                    type="button"
                  >
                    {createOrderLoading ? (
                      <div className="flex items-center gap-2">
                        <Spin size="small" /> <span>Placing Order...</span>
                      </div>
                    ) : (
                      'Place Order'
                    )}
                  </button>
                  <div className="md:hidden mt-4">
                    <TrustBar />
                  </div>
                  <p className="text-xs text-gray-400 text-center mt-3">By placing order you agree to our Terms of Service.</p>
                </div>
              </div>
            </section>
          </div>
        </div>
      </main>
      <div className="fixed bottom-0 left-0 w-full bg-white border-t border-gray-200 z-50 md:hidden pb-[env(safe-area-inset-bottom)]">
        <CheckoutPageNav handlePlaceOrder={handlePlaceOrder} createOrderLoading={createOrderLoading} />
      </div>
    </div>
  );
};

const CheckoutPageNav = ({ handlePlaceOrder, createOrderLoading }) => {
  return (
    <div className="fixed bottom-0 left-0 w-full bg-white border-t border-gray-200 px-4 py-2 z-50 flex flex-col items-center justify-center max-h-[85px] shadow-[0_-2px_8px_rgba(0,0,0,0.06)]">
      <button
        className="w-full bg-black text-white font-bold py-3 rounded-xl text-sm shadow-md hover:shadow-lg active:scale-[0.97] transition-all duration-150 flex items-center justify-center gap-2 disabled:opacity-70"
        onClick={handlePlaceOrder}
        disabled={createOrderLoading}
      >
        {createOrderLoading ? (
          <div className="flex items-center gap-2">
            <Spin size="small" /> <span>Placing Order...</span>
          </div>
        ) : (
          'Place Order'
        )}
      </button>
      <p className="text-[10px] text-gray-400 text-center mt-1 leading-tight">By placing this order, you agree to our Terms of Service</p>
    </div>
  );
};

export default CheckoutPage;